import { useState } from 'react'
import {
  createSearchParams,
  URLSearchParamsInit,
  useInRouterContext,
  useSearchParams,
} from 'react-router-dom'

/**
 * react-router-style `useSearchParams` that works both inside and outside a
 * `<Router>`. Inside a router, delegates to react-router. Outside, falls back
 * to local state seeded from the URL + `window.history.replaceState`.
 *
 * `useInRouterContext()` is stable for the lifetime of any component that
 * calls this hook — a component cannot be moved into or out of a router
 * context mid-mount — which is what makes the conditional hook call safe.
 */
export const useSearchParamsWithoutRequiringReactRouter = (
  defaultInit?: URLSearchParamsInit,
) => {
  const inRouter = useInRouterContext()
  /* eslint-disable react-hooks/rules-of-hooks -- fixed per mount */
  return inRouter
    ? useSearchParams(defaultInit)
    : useLocalSearchParams(defaultInit)
  /* eslint-enable react-hooks/rules-of-hooks */
}

type SetSearchParams = (
  next: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
) => void

const initialLocalParams = (defaultInit?: URLSearchParamsInit) => {
  const fromUrl = new URLSearchParams(window.location.search)
  return new URLSearchParams([
    // URL wins over defaults, as react-router does
    ...[...createSearchParams(defaultInit)].filter(
      ([key]) => !fromUrl.has(key),
    ),
    ...fromUrl,
  ])
}

const useLocalSearchParams = (defaultInit?: URLSearchParamsInit) => {
  const [searchParams, setSearchParamsState] = useState(() =>
    initialLocalParams(defaultInit),
  )
  const setSearchParams: SetSearchParams = (next) => {
    const params = createSearchParams(
      typeof next === 'function' ? next(searchParams) : next,
    )
    setSearchParamsState(params)
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`,
    )
  }
  return [searchParams, setSearchParams] as const
}
